<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import ClientIntegrationSettings
        from '$lib/components/drawers/client/integrations/ClientIntegrationSettings.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import SyncSettings from '$lib/components/drawers/client/integrations/SyncSettings.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientId: number;
  export let clientKey: string | undefined;
  export let organisationId: string | undefined;

  type MyobIntegration = {
    myobBusinessId: string;
    myobBusinessName: string;
    myobUid: string;
    myobUsername: string;
    canSync: boolean;
    canInvoicePost: boolean;
    canExpenseClaimPost: boolean;
    syncTaxCodes: boolean;
    syncSuppliers: boolean;
    syncGeneralLedgers: boolean;
    syncBudgets: boolean;
    syncSpends: boolean;
    syncSubAllocations: boolean;
    timeZoneId: string;
    financialYearEndMonth: number;
    budgetEndpoint: string;
    spendEndpoint: string;
    syncSpendPeriod: string;
    syncBudgetPeriod: string;
    lastSyncRunUtc: string | null;
  };

  type EntityRow = {
    id: number;
    key: string;
    name: string;
    code: string;
    integration: MyobIntegration | null;
  };

  type Form = {
    myobBusinessId: string;
    myobBusinessName: string;
    myobUid: string;
    myobUsername: string;
    timeZoneId: string;
    financialYearEndMonth: number;
    budgetEndpoint: string;
    spendEndpoint: string;
    syncSpendPeriod: string;
    syncBudgetPeriod: string;
    syncSettings: Record<string, boolean>;
  };

  const customSyncSettings = [
    { key: 'syncGeneralLedgers', label: 'GL Codes' },
    { key: 'syncSubAllocations', label: 'Sub Allocations' },
  ];
  const generalLedgerEndpoints = ['GeneralLedgerAccount', 'GeneralLedgerJob'];
  const syncPeriods = ['CalendarYear', 'CalendarMonth', 'FinancialYear'];

  let entities: EntityRow[] = [];
  let forms: Record<number, Form> = {};
  let expandedId: number | null = null;
  let isLoading = false;
  let isRefreshing = false;
  let isForcingSync = false;
  let isAuthenticating: Record<number, boolean> = {};
  let isSaving: Record<number, boolean> = {};

  $: hasConfiguredEntities = entities.some((entity) => entity.integration);

  onMount(load);

  async function load(preserveScroll = false, showLoading = true) {
    if (isLoading || isRefreshing) return;
    if (showLoading) {
      isLoading = true;
    } else {
      isRefreshing = true;
    }
    const scrollY = preserveScroll ? window.scrollY : 0;
    try {
      const response = await fetchWithAuth(`/api/myob-account-right/entities/${clientId}`);
      if (response.ok) {
        entities = await response.json();
      } else {
        showToast({ title: 'Error', message: 'Failed to load MYOB AccountRight entities', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to load MYOB AccountRight entities', type: 'error' });
    } finally {
      isLoading = false;
      isRefreshing = false;
      if (preserveScroll) requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
    }
  }

  function openRow(entity: EntityRow) {
    if (expandedId === entity.id) {
      expandedId = null;
      return;
    }

    expandedId = entity.id;
    const integration = entity.integration;
    if (!integration) return;

    forms[entity.id] = {
      myobBusinessId: integration.myobBusinessId,
      myobBusinessName: integration.myobBusinessName,
      myobUid: integration.myobUid,
      myobUsername: integration.myobUsername,
      timeZoneId: integration.timeZoneId,
      financialYearEndMonth: integration.financialYearEndMonth,
      budgetEndpoint: integration.budgetEndpoint,
      spendEndpoint: integration.spendEndpoint,
      syncSpendPeriod: integration.syncSpendPeriod,
      syncBudgetPeriod: integration.syncBudgetPeriod,
      syncSettings: {
        canSync: integration.canSync,
        canInvoicePost: integration.canInvoicePost,
        canExpenseClaimPost: integration.canExpenseClaimPost,
        syncTaxCodes: integration.syncTaxCodes,
        syncSuppliers: integration.syncSuppliers,
        syncGeneralLedgers: integration.syncGeneralLedgers,
        syncBudgets: integration.syncBudgets,
        syncSpends: integration.syncSpends,
        syncSubAllocations: integration.syncSubAllocations,
      },
    };
  }

  async function save(entity: EntityRow, isAutoSave = false) {
    const form = forms[entity.id];
    if (!form || !clientKey) return;

    isSaving = { ...isSaving, [entity.id]: true };
    try {
      const response = await fetchWithAuth(`/api/myob-account-right/config/${clientId}/${entity.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          myobBusinessId: form.myobBusinessId,
          myobBusinessName: form.myobBusinessName,
          myobUid: form.myobUid,
          myobUsername: form.myobUsername,
          canSync: form.syncSettings.canSync,
          canInvoicePost: form.syncSettings.canInvoicePost,
          canExpenseClaimPost: form.syncSettings.canExpenseClaimPost,
          syncTaxCodes: form.syncSettings.syncTaxCodes,
          syncSuppliers: form.syncSettings.syncSuppliers,
          syncGeneralLedgers: form.syncSettings.syncGeneralLedgers,
          syncBudgets: form.syncSettings.syncBudgets,
          syncSpends: form.syncSettings.syncSpends,
          syncSubAllocations: form.syncSettings.syncSubAllocations,
          timeZoneId: form.timeZoneId,
          financialYearEndMonth: Number(form.financialYearEndMonth),
          budgetEndpoint: form.budgetEndpoint,
          spendEndpoint: form.spendEndpoint,
          syncSpendPeriod: form.syncSpendPeriod,
          syncBudgetPeriod: form.syncBudgetPeriod,
        }),
      });

      if (response.ok) {
        showToast({ title: 'Success', message: isAutoSave ? 'MYOB AccountRight sync settings saved' : 'MYOB AccountRight configuration saved', type: 'success' });
        await load(true, false);
      } else {
        showToast({ title: 'Error', message: isAutoSave ? 'Failed to save MYOB AccountRight sync settings' : 'Failed to save MYOB AccountRight configuration', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: isAutoSave ? 'Failed to save MYOB AccountRight sync settings' : 'Failed to save MYOB AccountRight configuration', type: 'error' });
    } finally {
      isSaving = { ...isSaving, [entity.id]: false };
    }
  }

  async function connect(entity: EntityRow) {
    if (!clientKey) return;

    const popup = window.open('', `myob-authentication-${entity.id}`, 'popup,width=640,height=760');
    if (!popup) {
      showToast({ title: 'Error', message: 'Allow pop-ups to connect MYOB AccountRight', type: 'error' });
      return;
    }

    isAuthenticating = { ...isAuthenticating, [entity.id]: true };
    try {
      const query = new URLSearchParams({
        clientId: clientId.toString(),
        clientKey,
        entityId: entity.id.toString(),
        entityKey: entity.key,
      });
      const response = await fetchWithAuth(`/api/myob-account-right/authentication-url?${query}`);
      if (!response.ok) {
        popup.close();
        showToast({ title: 'Error', message: 'Failed to start MYOB AccountRight authentication', type: 'error' });
        return;
      }

      popup.location.assign(await response.text());
      const popupCheck = window.setInterval(async () => {
        if (!popup.closed) return;

        window.clearInterval(popupCheck);
        isAuthenticating = { ...isAuthenticating, [entity.id]: false };
        await load();
      }, 500);
    } catch {
      popup.close();
      isAuthenticating = { ...isAuthenticating, [entity.id]: false };
      showToast({ title: 'Error', message: 'Failed to start MYOB AccountRight authentication', type: 'error' });
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/myob-account-right/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok
          ? { title: 'Success', message: 'MYOB AccountRight sync has been queued', type: 'success' }
          : { title: 'Error', message: 'Failed to queue MYOB AccountRight sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue MYOB AccountRight sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-1 text-xl font-semibold">MYOB AccountRight Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure each connected MYOB AccountRight entity.</p>

  <ClientIntegrationSettings basePath="/api/myob-account-right" {clientId} />
  <div class="mb-4 flex justify-end gap-2">
    <Button label={isForcingSync ? 'Queueing...' : 'Force sync'} onClick={forceSync} disabled={isLoading || isForcingSync || !hasConfiguredEntities} size="small" />
    <Button label={isLoading ? 'Loading...' : 'Refresh'} onClick={load} disabled={isLoading || isForcingSync} colour="secondary" size="small" />
  </div>

  <h4 class="mb-3 text-lg font-semibold text-gray-800">Entities</h4>
  {#if isLoading}
    <p class="text-sm text-gray-500">Loading entities...</p>
  {:else if entities.length === 0}
    <p class="text-sm text-gray-500">No entities found for this client.</p>
  {:else}
    <div class="space-y-2">
      {#each entities as entity (entity.id)}
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          <button type="button" class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100" on:click={() => openRow(entity)}>
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">{entity.name}</span>
              {#if entity.integration}
                <span class="text-xs text-gray-500">{entity.integration.myobBusinessName}</span>
                <span class="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Configured</span>
              {:else}
                <span class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">Not configured</span>
              {/if}
            </div>
            <span class="ml-2 text-sm text-gray-400">{expandedId === entity.id ? '▲' : '▼'}</span>
          </button>

          {#if expandedId === entity.id}
            <div class="border-t border-gray-200 px-4 pb-4 pt-4">
              {#if !entity.integration}
                <div class="space-y-3">
                  <p class="text-sm text-gray-600">Connect this entity to MYOB AccountRight to configure it.</p>
                  <div class="flex justify-end">
                    <Button
                      label={isAuthenticating[entity.id] ? 'Connecting...' : 'Connect MYOB AccountRight'}
                      onClick={() => connect(entity)}
                      disabled={isAuthenticating[entity.id] || !clientKey}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>
              {:else if forms[entity.id]}
                <div class="space-y-6">
                  <SyncSettings
                    settings={forms[entity.id].syncSettings}
                    customSettings={customSyncSettings}
                    disabled={isSaving[entity.id]}
                    isSaving={isSaving[entity.id]}
                    onChange={() => save(entity, true)}
                  />

                  <div class="space-y-4">
                    <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">MYOB Connection Settings</h5>
                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="myob-business-name-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Business Name</label>
                        <FieldHint text="The MYOB company name shown to staff." />
                        <input
                          id="myob-business-name-{entity.id}"
                          bind:value={forms[entity.id].myobBusinessName}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="myob-business-id-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Business ID</label>
                        <FieldHint text="The MYOB business identifier used for API requests." />
                        <input
                          id="myob-business-id-{entity.id}"
                          bind:value={forms[entity.id].myobBusinessId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="myob-uid-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">MYOB UID</label>
                        <FieldHint text="The authenticated MYOB user identifier." />
                        <input
                          id="myob-uid-{entity.id}"
                          bind:value={forms[entity.id].myobUid}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="myob-username-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">MYOB Username</label>
                        <FieldHint text="The username associated with the MYOB connection." />
                        <input
                          id="myob-username-{entity.id}"
                          bind:value={forms[entity.id].myobUsername}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Budget and Spend Settings</h5>
                    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div>
                        <label for="myob-timezone-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Time Zone</label>
                        <FieldHint text="Used to interpret MYOB dates and times for this entity." />
                        <input
                          id="myob-timezone-{entity.id}"
                          bind:value={forms[entity.id].timeZoneId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="myob-financial-year-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Financial Year End Month</label>
                        <FieldHint text="The month number when this entity's financial year ends." />
                        <input
                          id="myob-financial-year-{entity.id}"
                          type="number"
                          min="1"
                          max="12"
                          bind:value={forms[entity.id].financialYearEndMonth}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="myob-budget-endpoint-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Budget Endpoint</label>
                        <FieldHint text="The MYOB general ledger source used for budget data." />
                        <select
                          id="myob-budget-endpoint-{entity.id}"
                          bind:value={forms[entity.id].budgetEndpoint}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                          >{#each generalLedgerEndpoints as endpoint}<option value={endpoint}>{endpoint}</option>{/each}</select
                        >
                      </div>
                      <div>
                        <label for="myob-spend-endpoint-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Spend Endpoint</label>
                        <FieldHint text="The MYOB general ledger source used for spend data." />
                        <select
                          id="myob-spend-endpoint-{entity.id}"
                          bind:value={forms[entity.id].spendEndpoint}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                          >{#each generalLedgerEndpoints as endpoint}<option value={endpoint}>{endpoint}</option>{/each}</select
                        >
                      </div>
                      <div>
                        <label for="myob-spend-period-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Spend Period</label>
                        <FieldHint text="How spend data is grouped when it is synced." />
                        <select
                          id="myob-spend-period-{entity.id}"
                          bind:value={forms[entity.id].syncSpendPeriod}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                          >{#each syncPeriods as period}<option value={period}>{period}</option>{/each}</select
                        >
                      </div>
                      <div>
                        <label for="myob-budget-period-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Budget Period</label>
                        <FieldHint text="How budget data is grouped when it is synced." />
                        <select
                          id="myob-budget-period-{entity.id}"
                          bind:value={forms[entity.id].syncBudgetPeriod}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                          >{#each syncPeriods as period}<option value={period}>{period}</option>{/each}</select
                        >
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-end">
                    <Button
                      label={isSaving[entity.id] ? 'Saving...' : 'Save'}
                      onClick={() => save(entity)}
                      disabled={!!isSaving[entity.id] || !clientKey}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
